<template>
	<div class>
		<!-- Modal Component -->
		<ModalBox
			v-model="legacyModalVisible"
			placement="left"
			size="md"
			scroll-body
			hide-close
			header-class="!hidden"
			:wrapper-classes="'add-platform'"
		>
			<div class="modal-container">
				<div class="px-3 pt-4 pb-3 flex items-start justify-between gap-3">
					<div class="min-w-0">
						<h3 class="text-lg font-semibold text-white mb-0.5">New destination</h3>
						<p class="text-tiny text-surface-7 mb-0">Choose where to send your stream.</p>
					</div>
					<form-button type="link" isIcon @click="dismiss" class="shrink-0 -mt-1">
						<icon-base>
							<icon-close />
						</icon-base>
					</form-button>
				</div>
				<!-- Source toggle: create a brand-new destination vs reuse a saved one -->
				<div v-if="savedDestinations.length" class="px-3 pb-2">
					<TabGroup type="segmented" :cols="2">
						<TabItem :is-active="addMode === 'new'" @click="addMode = 'new'">Add new</TabItem>
						<TabItem :is-active="addMode === 'saved'" @click="addMode = 'saved'">From saved</TabItem>
					</TabGroup>
				</div>
				<div class="px-3 flex flex-wrap items-start">
					<!-- Loading skeleton: reserves the meter's space so it doesn't pop in -->
					<div v-if="!usageLoaded" class="w-full mb-3">
						<div class="h-9 rounded-lg bg-surface-1 animate-pulse"></div>
					</div>
					<!-- Case total destination.
					     One box in two tempers. The plan line is always the account's,
					     because destination slots are bought once and spent everywhere;
					     the workspace line underneath says how much of that is the
					     stream's own neighbourhood, so the bigger number never reads as
					     something this workspace did. Running out is the only state
					     worth colouring. -->
					<div class="w-full" v-else-if="platformUsage && platformUsage.totalDestination">
						<div
							class="rounded-lg ring-1 px-3 py-2.5 mb-3"
							:class="destinationUsage.nearLimit ? 'ring-gold/40 bg-gold/[0.06]' : 'ring-surface-3'"
						>
							<div class="flex items-center justify-between gap-3">
								<span class="flex items-center gap-1.5 min-w-0 text-tiny">
									<icon-base v-if="destinationUsage.nearLimit" class="shrink-0 text-gold" :width="14" :height="14">
										<icon-alert-triangle />
									</icon-base>
									<span class="truncate" :class="destinationUsage.nearLimit ? 'text-gold' : 'text-surface-7'">
										<span class="font-medium" :class="destinationUsage.nearLimit ? '' : 'text-surface-9'">
											{{ destinationUsage.used }} of {{ destinationUsage.total }}
										</span>
										destinations used on your plan
									</span>
								</span>
								<a
									class="text-tiny font-medium hover:underline shrink-0"
									:class="destinationUsage.nearLimit ? 'text-gold' : 'text-main'"
									target="_blank"
									href="/app/subscribe"
								>Upgrade</a>
							</div>
							<div v-if="showWorkspaceDestinations" class="flex items-center gap-1.5 mt-1 text-tiny text-surface-7">
								<ProjectDot :color="activeProjectColor" size="size-1.5" />
								<span class="truncate">{{ destinationUsage.inWorkspace }} in this workspace</span>
							</div>
						</div>
					</div>
					<!-- Case unique/extra destination -->
					<div class="w-full">
						<Alert type="normal" class="flex p-2 mb-3"
							v-if="platformUsage && platformUsage.uniqueDestination && platformUsage.uniqueDestination.limit">
							<div>
								<div class="text-dark-9 text-s-m text-500 m-0">You have used {{ platformUsage.uniqueDestination.used
								}}/{{
										platformUsage.uniqueDestination.limit }} unique destinations and {{
										platformUsage.extraDestination.used }}/{{ platformUsage.extraDestination.limit }} extra ones<span
										v-if="getStreamtype()"> across all {{ getStreamtype() }}</span>.</div>
								<div class="text-dark-7 text-s-s font-normal m-0">Need more? <a
										class="text-underline text-dark-8" target="_blank" href="/app/subscribe">Upgrade</a> here.</div>
							</div>
						</Alert>
					</div>
					<!-- Internal Workflow -->
					<div class="mb-3 w-full" v-if="addMode === 'new'">
						<form-button @click="selectInternalWorkflow"
							:custom-classes="'w-full flex items-center gap-3 rounded-xl p-3 text-left transition-all ring-1 ring-inset ' + (internalWorkflow ? '!bg-main-transparent !ring-main' : 'bg-surface-1 ring-white/[0.06] hover:bg-surface-3 hover:ring-white/15')">
							<div class="shrink-0 size-10 rounded-lg bg-main-transparent flex items-center justify-center">
								<img src="@/assets/castr-red-dot.svg" alt="Castr" class="w-5 h-5">
							</div>
							<div class="min-w-0 grow">
								<div class="flex items-center gap-2">
									<span class="text-sm font-semibold text-white">Stream within Castr</span>
									<span class="text-[10px] font-semibold uppercase tracking-wide text-main bg-main-transparent rounded px-1.5 py-0.5">Internal</span>
								</div>
								<p class="text-tiny text-surface-7 mb-0 truncate">Use another Castr livestream or transcoder as a destination.</p>
							</div>
							<icon-base class="shrink-0 text-surface-7" :width="18" :height="18" width-view-box="24" height-view-box="24" fill="none"><icon-chevron-right-stroke /></icon-base>
						</form-button>
					</div>
				</div>
				<!-- From saved: reuse an existing destination (link/unlink + remove) -->
				<div v-if="addMode === 'saved'" class="px-3 pb-3">
					<p class="text-tiny text-surface-7 mb-2 leading-snug">
						Select the destinations this stream should publish to. Deleting a destination removes it from every stream it's linked to.
					</p>
					<ul ref="savedListEl"
						class="list-none m-0 rounded-lg ring-1 ring-surface-3 divide-y divide-surface-3 max-h-[340px] overflow-y-auto">
						<li v-for="dest in orderedSavedDestinations" :key="dest._id"
							class="flex items-center gap-2.5 px-3 py-2"
							:class="{ 'opacity-40': isDestinationDisabled(dest) && renamingId !== dest._id }">
							<!-- Edit mode: name for any destination; server + key for custom RTMP/SRT -->
							<template v-if="renamingId === dest._id">
								<div class="flex-1 min-w-0">
									<FormInput v-model="editValues.name" class="mb-2" placeholder="Destination name"
										@keydown.enter="saveRename(dest)" @keydown.esc="cancelRename" />
									<template v-if="dest.template === 'custom'">
										<FormInput v-model="editValues.server" class="mb-2"
											placeholder="Server URL (rtmp://… or srt://…)"
											@keydown.enter="saveRename(dest)" @keydown.esc="cancelRename" />
										<FormInput v-model="editValues.key" class="mb-2" placeholder="Stream key (optional)"
											@keydown.enter="saveRename(dest)" @keydown.esc="cancelRename" />
									</template>
									<div class="flex items-center gap-2">
										<form-button size="sm" type="main"
											:disabled="savingRename || !editValues.name.trim() || (dest.template === 'custom' && !editValues.server.trim())"
											@click.stop="saveRename(dest)">{{ savingRename ? 'Saving…' : 'Save' }}</form-button>
										<form-button size="sm" type="secondary" :disabled="savingRename"
											@click.stop="cancelRename">Cancel</form-button>
									</div>
								</div>
							</template>
							<!-- Normal row -->
							<template v-else>
								<FormCheckbox :name="'link-' + dest._id" :input-value="dest._id"
									:disabled="isDestinationDisabled(dest)" :model-value="isDestinationLinked(dest)"
									@change="toggleDestinationLink(dest)" />
								<div class="min-w-0 flex-1">
									<p class="m-0 text-sm text-white truncate">{{ dest.name }}</p>
									<p class="m-0 text-tiny text-surface-8 truncate">{{ dest.server }}</p>
								</div>
								<Badge class="shrink-0" :title="dest.template" />
								<template v-if="savedDeleteConfirmId === dest._id">
									<form-button size="sm" type="link" class="!text-error-light shrink-0"
										@click.stop="confirmDeleteSaved(dest)">Delete</form-button>
									<form-button size="sm" type="link" class="!text-surface-8 shrink-0"
										@click.stop="savedDeleteConfirmId = null">Cancel</form-button>
								</template>
								<template v-else>
									<form-button size="sm" type="link" isIcon class="!text-surface-8 hover:!text-white shrink-0"
										title="Edit destination" @click.stop="startRename(dest)">
										<icon-base width="14" height="14" fill="transparent">
											<path
												d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Zm.176 1.412-.882.882 2.343 2.343.883-.882a.25.25 0 0 0 0-.354l-1.99-1.99a.25.25 0 0 0-.353 0Z"
												fill="currentColor" />
										</icon-base>
									</form-button>
									<form-button size="sm" type="link" isIcon class="!text-surface-8 hover:!text-error-light shrink-0"
										title="Remove destination" @click.stop="savedDeleteConfirmId = dest._id">
										<icon-base width-view-box="20" height-view-box="21" width="14" height="14" iconColor="none">
											<icon-bucket />
										</icon-base>
									</form-button>
								</template>
							</template>
						</li>
					</ul>
					<!-- Inline create: adds a reusable custom (RTMP) destination and links it -->
					<form-button v-if="!creatingSaved" type="secondary" class="w-full justify-center mt-3"
						@click="openCreateSaved">Add a reusable destination</form-button>
					<div v-else class="mt-3 rounded-lg ring-1 ring-surface-3 p-3">
						<p class="text-tiny font-medium text-surface-8 mb-2">New custom RTMP destination</p>
						<FormInput v-model="newSaved.name" placeholder="Destination name" class="mb-2" />
						<FormInput v-model="newSaved.server" placeholder="RTMP server URL (rtmp://…)" class="mb-2" />
						<FormInput v-model="newSaved.key" placeholder="Stream key (optional)" class="mb-3" />
						<div class="grid grid-cols-2 gap-2">
							<form-button type="main" fullWidth
								:disabled="savingNewSaved || !newSaved.name || !newSaved.server" @click="saveCreateSaved">
								{{ savingNewSaved ? 'Saving…' : 'Save destination' }}
							</form-button>
							<form-button type="secondary" fullWidth :disabled="savingNewSaved"
								@click="cancelCreateSaved">Cancel</form-button>
						</div>
						<p class="text-tiny text-surface-7 mt-2 mb-0">Connecting YouTube, Facebook or another platform?
							<form-button custom-classes="text-main hover:underline" @click="addMode = 'new'">Add new</form-button>
						</p>
					</div>
				</div>
				<div class="flex flex-wrap" v-if="platformListProcessing && addMode === 'new'">
					<Spinner text="Retrieving data..." classes="text-dark-8 my-3" spinner-color="var(--c-dark-8)"
						spinner-size="15px" />
				</div>
				<div class="px-3" v-if="!platformListProcessing && addMode === 'new'">
					<p class="text-tiny font-medium text-surface-7 mb-2">Or choose a platform</p>
				</div>
				<div class="px-3 sticky top-0 z-10 bg-surface-2 pt-1 pb-3" v-if="!platformListProcessing && addMode === 'new'">
					<div class="relative">
						<icon-base class="absolute left-3 top-1/2 -translate-y-1/2 text-surface-7 pointer-events-none z-10">
							<icon-search />
						</icon-base>
						<FormInput v-model="searchQuery" placeholder="Search platforms…" customClasses="!pl-9" />
					</div>
				</div>
				<ul class="platform-list list-none flex flex-wrap -mx-1.5 px-3" v-if="!platformListProcessing && addMode === 'new'">
					<li class="w-1/2 md:w-1/3 p-1.5" v-for="(plat, index) in gridPlatforms" :key="index">
						<div @click="plat.isCustom ? selectPlatform() : selectPlatform(plat)"
							class="w-full h-full flex flex-col items-center justify-center gap-2.5 rounded-xl bg-surface-1 ring-1 ring-inset ring-white/[0.06] hover:bg-surface-3 hover:ring-white/10 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/30 transition-all duration-150 p-3 min-h-[104px] cursor-pointer"
							:class="{ '!bg-main-transparent !ring-main': plat.isCustom ? customPlatform : plat.name === platform.name }">
							<div class="h-8 flex items-center justify-center text-white">
								<icon-base v-if="plat.isCustom" :width="24" :height="24" width-view-box="24" height-view-box="24" fill="none"><icon-settings /></icon-base>
								<img v-else class="max-h-8 max-w-[90%] object-contain" :src="coloredIcon(plat)">
							</div>
							<div class="text-tiny text-surface-9 text-center capitalize leading-tight">
								{{ plat.isCustom ? 'Custom' : (plat.name === 'twitter' ? 'X (Twitter)' : plat.name) }}
							</div>
						</div>
					</li>
				</ul>
				<div v-if="noPlatformResults && !platformListProcessing && addMode === 'new'" class="text-center py-10 px-3 text-surface-7">
					<p class="text-sm">No platforms match "{{ searchQuery }}".</p>
				</div>
				<div class="flex flex-wrap">
					<div class="w-full">
						<div v-if="stream.type === 'unavail'" class="mt-4">
							<p class="text-center font-normal text-s-md mt-4">Want to stream to your website too?</p>
							<p class="text-center font-normal text-s-md">Check out our <a class="link-main font-bolder"
									href="#">demo</a> of the Live Stream product.</p>
						</div>
					</div>
				</div>
			</div>

		</ModalBox>

		<Teleport to="body">
			<section
				v-if="legacyModalVisible"
				class="second-modal"
				:class="{ showform: stage >= 1, expand: internalWorkflow }"
			>
				<Transition name="second-modal-swap" mode="out-in">
				<div v-if="internalWorkflow" key="internal-workflow">
					<add-castr-stream-2 :stream="stream" @platform-saved="onCastrStreamPlatformSaved"
						@dismiss="unselectPlatform" />
				</div>
				<div v-else class="second-modal-content" :key="customPlatform ? 'custom' : (platform.name || 'none')">
					<div class="flex items-center gap-3 bg-surface-3 px-3 py-3">
						<form-button type="link" isIcon @click="unselectPlatform" title="Back to platforms">
							<icon-base fill="none">
								<icon-chevron-left />
							</icon-base>
						</form-button>
						<img v-if="!customPlatform" class="max-h-7 max-w-[110px] object-contain shrink-0" :src="coloredIcon(platform)">
						<h3 class="text-base font-medium text-white capitalize truncate mb-0">
							{{ customPlatform ? 'Custom Destination' : (platform.name === 'twitter' ? 'X (Twitter)' : platform.name) }}
						</h3>
					</div>
					<div class="px-4 flex flex-wrap">
						<div class="w-full">
							<Alert v-if="error" type="error" class="mt-3">
								<div>{{ error.message }}</div>
								<div v-if="error.subscription" class="text-center">
									<FormButton type="link" size="sm" href="/app/subscribe">
										<strong>upgrade now</strong>
									</FormButton>
								</div>
							</Alert>
						</div>
					</div>
					<div class="px-4 mt-4 flex flex-wrap">
						<div class="w-full">
							<div v-if="!configRequired" class="text-center">
								<div v-if="serviceLinkProcessing">
									<p v-if="serviceLinkError && serviceLinkConflict">
										This <span class="text-capitalize">{{ platform.name }}</span> account is already linked with this
										stream.
									</p>
									<p v-else-if="serviceLinkError">
										Sorry, we couldn't link the specified <span class="text-capitalize">{{ platform.name }}</span> account.
									</p>
									<p v-else>
										<span v-if="linkedServiceCreds"><span class="text-capitalize">{{ platform.name }}</span> connected —
											saving destination…</span>
										<span v-else>Connecting to <span class="text-capitalize">{{ platform.name }}</span></span>
										&nbsp;
										<span class="inline-block size-4 align-middle rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
									</p>
									<form-button type="secondary" v-if="!linkedServiceCreds || serviceLinkError"
										@click="cancelServiceLinking">
										Cancel
									</form-button>
								</div>
								<div v-else>
									<div>
										<p class="text-s-m text-dark-8 mb-2">Connect my <span class="text-capitalize">{{
											platform.name
										}}</span> account</p>
										<p v-if="isServiceAlreadyLinked()" class="text-danger mb-2">
											<span class="text-capitalize">{{ platform.name }}</span> is already linked to this stream.
										</p>
										<a v-else target="_blank" @click="getServiceOAuthUrl()">
											<form-button class="w-full justify-center" :disabled="isSchedulerInfiniteLoop"
												v-if="platform.name === 'facebook live'" @click="onServiceAuthURLTap">
												Continue With Facebook
											</form-button>
											<form-button class="w-full justify-center" :disabled="isSchedulerInfiniteLoop" v-else
												@click="onServiceAuthURLTap">
												Connect
												to&nbsp;<span class="text-capitalize">{{ platform.slug || platform.name }}</span>
											</form-button>
										</a>
										<p class="text-s-s text-dark-6" v-if="isSchedulerInfiniteLoop">Due to a Facebook restriction, Infinite
											Loop mode cannot be used with this option.</p>
									</div>
									<div class="text-center text-surface-7 text-xs uppercase tracking-wide mt-3 mb-3">or</div>
									<div>
										<p class="text-dark-8 text-s-m mb-2">Enter a server and stream key</p>
										<form-button class="w-full justify-center"
											:disabled="isBroadcastSubscription && platform.name === 'facebook live'"
											@click="ignoreServiceLinking">
											Set up with server/key
										</form-button>
										<div v-if="isBroadcastSubscription && platform.name === 'facebook live'">
											Use a custom RTMP destination to add a Facebook Page/Group, or upgrade to use this feature.
										</div>
									</div>

									<div class="mt-3">
										<form-button class="w-full justify-center" type="secondary" @click="unselectPlatform">
											Cancel
										</form-button>
									</div>
									<div class="doc mt-2">
										<a class="text-s-s text-dark-6 inline-flex items-center" target="_blank"
											href="http://docs.castr.com/en/articles/2291793-how-to-connect-facebook-live-with-castr"
											v-if="platform.name === 'facebook live'"><img src="@/assets/images/book.png" class="mr-2"
												alt="Doc"> How to connect
											Facebook to Castr</a>
										<a class="text-s-s text-dark-6 inline-flex items-center" target="_blank"
											href="https://docs.castr.com/en/articles/5369538-how-to-connect-youtube-with-castr"
											v-if="platform.name === 'youtube events'"><img src="@/assets/images/book.png" class="mr-2"
												alt="Doc"> How to
											connect YouTube to Castr</a>
									</div>
								</div>
							</div>
							<div v-else>
								<div class="field-container mt-4 flex flex-wrap" v-if="customPlatform">
									<div class="px-3 md:px-3 py-2 w-full">
										<TabGroup type="segmented" :cols="2">
											<TabItem :is-active="customMode === 'RTMP'" @click="onCustomModeChanged('RTMP')">RTMP</TabItem>
											<TabItem :is-active="customMode === 'SRT'" @click="onCustomModeChanged('SRT')">SRT</TabItem>
										</TabGroup>
									</div>
								</div>

								<div v-if="customPlatform && customMode === 'SRT'" class="field-container mt-4">
									<!-- host -->
									<FloatingInput surface="2" dense label="Host" v-model="customSrtPlatform.host" hint="srt-ingest.example.com"
										class="mb-3" @keypress="onInputChange('host')" @keyup="onInputChange('host')"
										:has-error="isError('host')" :helper="errorText('host')" />
									<!-- port -->
									<FloatingInput surface="2" dense label="Port" v-model="customSrtPlatform.port" hint="9998" inputmode="numeric"
										class="mb-3" @keydown="onInputChange('port')" @keyup="onInputChange('port')" :has-error="isError('port')"
										:helper="errorText('port')" />

									<!-- stream id -->
									<FloatingInput surface="2" dense label="Stream ID" v-model="customSrtPlatform.streamId" hint="my-stream-id" class="mb-3"
										@keypress="onInputChange('streamId')" @keyup="onInputChange('streamId')"
										:has-error="isError('streamId')" :helper="errorText('streamId')" />
									<!-- latency -->
									<FloatingInput surface="2" dense label="Latency" v-model="customSrtPlatform.latency" hint="120" inputmode="numeric" class="mb-3"
										@keypress="onInputChange('latency')" @keyup="onInputChange('streamId')"
										:has-error="isError('latency')" :helper="errorText('latency')" />
									<FloatingInput surface="2" dense label="Passphrase" v-model="customSrtPlatform.passphrase" hint="optional secret phrase"
										class="mb-3" @keypress="onInputChange('passphrase')" @keyup="onInputChange('passphrase')"
										:has-error="isError('passphrase')" :helper="errorText('passphrase')" />
									<SrtPidMapping protocol="srt" :ingest-kind="srtIngestKind"
										v-model:pids="customSrtPlatform.pids" v-model:pmtPid="customSrtPlatform.pmtPid"
										:host="customSrtPlatform.host" :port="customSrtPlatform.port" :stream-id="customSrtPlatform.streamId"
										:latency="customSrtPlatform.latency" :passphrase="customSrtPlatform.passphrase"
										@update:valid="srtPidsValid = $event" />
								</div>

								<div v-else-if="shouldConfigureServer()" class="field-container mt-4">
									<p v-show="formErrors.server" class="text-danger">
										<!-- specify {{ customPlatform && 'destination' || platform.name }} server -->
										Invalid {{ customPlatform && 'destination' || platform.name }} ingest address.
									</p>

									<!-- <input v-if="customPlatform || platform.customServer" -->
									<div v-if="checkServerInputMode('input')">
										<FloatingInput surface="2" dense :label="customPlatform ? 'Server URL' : 'Streaming server'"
											v-model="platform.server"
											:hint="platform.serverInputPlaceholder || 'rtmp://ingest.example.com/live'" class="mb-3"
											@keypress="onInputChange('server')" @keyup="onInputChange('server')" :has-error="isError('server')"
											:helper="errorText('server')" />
										<div v-if="customPlatform">
											<p v-show="customSrtPlatformErrors.server && !formErrors.server" class="text-danger">
												{{ customSrtPlatformErrors.server }}
											</p>
											<FormCheckbox class="mb-2" v-model="isAuthRequired" :inputValue="true" name="is-auth-required" label="Authentication Required" />
											<div v-if="isAuthRequired">
												<FloatingInput surface="2" dense label="Username" v-model="rtmpAuthUsername" class="mb-3" />
												<FloatingInput surface="2" dense label="Password" v-model="rtmpAuthPassword" type="password" class="mb-3"
													@blur="checkPassword" :has-error="!isRtmpPassValid"
													:helper="!isRtmpPassValid ? '@ symbol is not supported in password, please change the password.' : ''" />
											</div>
										</div>
									</div>
									<div v-else-if="checkServerInputMode('chunks')" class="input-container"
										@click="focusServerKeySegment">
										<div v-for="(segment, index) in platform.serverKeySegments" :key="index">
											<FormInput v-if="segment.input" v-model="platform.serverKeySegmentValues[index]"
												:placeholder="segment.placeholder" class="mb-2" @change="computePlatformServerFromSegments"
												@keypress="onInputChange('server')" />
											<div v-else-if="!segment.hidden" class="value-placeholder">
												{{ segment.text || segment }}
											</div>
										</div>
									</div>
									<drop-down-menu v-else-if="checkServerInputMode('select')" id="dropdown-1" type="flat"
										class="region-dropdown w-full mb-2" listClasses="dropdown-menu_md test w-full mt-0" size="md">
										<template #toggle-button>
											<div class="flex items-center w-full">
												<span class="font-normal text-s-m" v-if="chosenServer">
													<span>{{ chosenServer }}</span>
												</span>
												<span class="text-dark-8 font-normal text-s-m" v-else>Select one</span>
												<icon-base class="ml-auto flex-shrink-0" :width="10" :height="6" width-view-box="10" height-view-box="6"><icon-caret-down /></icon-base>
											</div>
										</template>

										<template #menu-items>
											<drop-down-menu-item :key="server.id" v-for="server in platform.servers"
												@click="selectServer(server)">{{ server.text
												}}
											</drop-down-menu-item>
										</template>
									</drop-down-menu>
								</div>
								<p class="text-dark-7 text-size-m" v-if="linkedServiceCreds">
									Some ingest details were captured from the connected <span class="text-capitalize">{{ platform.name }}</span>
									account.
								</p>
								<div v-else-if="customMode !== 'SRT'" class="field-container mt-4">
									<FloatingInput surface="2" dense label="Stream key" v-model="platform.streamKey"
										:hint="platform.streamKeyPlaceholder || 'xxxx-xxxx-xxxx-xxxx'" class="mb-3"
										@keypress="onInputChange('streamKey')" @keyup="onInputChange('streamKey')" />
								</div>

								<!-- stream metadata form -->
								<div v-if="canConfigureMetadata()">
									<div
										v-if="platform.serviceAuthorization.subChannelRequired && !platform.serviceAuthorization.autoSelectDefaultSubChannel"
										class="field-container mt-4">
										<label>
											{{ platform.serviceAuthorization.subChannelFieldLabel || 'Publishing Channel' }}
										</label>
										<drop-down-menu ref="subChannelDropdown" id="service-subchannels-dropdown" type="flat" listClasses="bg-surface-3 border border-surface-4 rounded-lg mt-1" fullWidth @show="onSubchannelListToggle(true)"
											@hide="onSubchannelListToggle(false)">
											<template #toggle-button>
													<div class="basis-[90%] max-w-[90%] truncate text-left text-tiny text-surface-text-1">
														{{ selectedSubChannel?.name || 'Select one' }}
													</div>
													<icon-base class="ml-auto">
														<icon-chevron-down />
													</icon-base>
											</template>

											<template #menu-items>
												<drop-down-menu-item customClasses="!h-9 flex justify-between items-center p-2 hover:bg-surface-4 w-full relative" v-for="(subchannel, index) in platform.subChannelList" :key="subchannel.id" :class="{
													active: selectedSubChannel && subchannel.id === selectedSubChannel.id,
													disabled: isSubChannelDisabled(subchannel)
												}" @click="changeSubChannelSelection(subchannel)" >
													<div class="text-tiny text-surface-text-1 basis-[90%] max-w-[90%] truncate text-left">
														{{ subchannel.name }}
													</div>
													<div v-show="subchannel.type" class="text-xs text-surface-6">
														<span v-if="showFacebookIcon(subchannel)">
															<icon-base class="w-4 h-4">
																<icon-facebook-page />
															</icon-base>
														</span>
														<span class="text-surface-6 text-xs capitalize" v-else>{{ subchannel.type }}</span>
                            <hr class="border-surface-4 absolute bottom-0 left-1 right-0 w-[calc(100%-8px)]" v-if="index !== platform.subChannelList.length - 1" />
													</div>
													<div class="hint-upgrade text-xs color-danger absolute bottom-1 right-2">
														Not available on Trial plan
													</div>
												</drop-down-menu-item>
											</template>
										</drop-down-menu>
									</div>

									<!-- <div v-if="hasMetaProp('title')" class="field-container"> -->
									<div v-if="canConfigureMetadata('title') || platform.name === 'youtube events'"
										class="field-container mt-4">
										<FormInput
											:label="'Stream Title' + (platform.name === 'youtube events' ? ' *' : '') + (showTitleLimitTracking ? ' (' + platform.configurableMetadata.title.length + '/100)' : '')"
											v-model="platform.configurableMetadata.title" :maxlength="maxLengthTitle"
											placeholder="Enter stream title" class="mb-2" />
									</div>

									<!-- <div v-if="hasMetaProp('description')" class="field-container"> -->
									<div v-if="canConfigureMetadata('description') || platform.name === 'youtube events'"
										class="field-container mt-4">
										<FormText :label="'Stream Description ' + (showTitleLimitTracking ? '(' + platform.configurableMetadata.description.length + '/5000)' : '')" class="mb-3" v-model="platform.configurableMetadata.description"
											:maxlength="maxLengthDescription" placeholder="Enter stream description" />

									</div>

									<div v-if="platform.name === 'youtube events'" class="field-container mt-4 mb-4">
										<label for="">
											Privacy Status
										</label>

										<drop-down-menu ref="youtubePrivacyDropdownInDialog" type="secondary"
											customClasses="w-full text-sm h-8 bg-surface-3 hover:bg-surface-4 rounded-md flex items-center px-3 capitalize text-tiny"
											customDropdownClass="w-full">
											<template #toggle-button>
												{{ platform.configurableMetadata.privacyStatus || selectedPrivacyStatus }}
												<icon-base class="ml-auto">
													<icon-chevron-down />
												</icon-base>
											</template>
											<template #menu-items>
												<drop-down-menu-item
													customClasses="max-w-full w-full h-auto flex flex-col text-wrap text-left items-start px-3 py-2 text-tiny hover:bg-surface-4"
													@click="onPrivacySettingsChanged('public', platform)">
													Public
												</drop-down-menu-item>
												<drop-down-menu-item
													customClasses="max-w-full w-full h-auto flex flex-col text-wrap text-left items-start px-3 py-2 text-tiny hover:bg-surface-4"
													@click="onPrivacySettingsChanged('unlisted', platform)">
													Unlisted
												</drop-down-menu-item>
												<drop-down-menu-item
													customClasses="max-w-full w-full h-auto flex flex-col text-wrap text-left items-start px-3 py-2 text-tiny hover:bg-surface-4"
													@click="onPrivacySettingsChanged('private', platform)">
													Private
												</drop-down-menu-item>
											</template>
										</drop-down-menu>

									</div>

									<div v-if="platform.name === 'youtube events'" class="field-container mt-4 mb-4">
										<div class="flex items-center gap-2">
											<FormSwitch v-model="enableAdsOnStream" />
											<div class="flex items-center text-[13px] text-[#D1DBF0]">
												Run ads on stream
												<Tooltip content="Enable to insert pre-roll and mid-roll ads">
													<icon-base class="ml-1 cursor-pointer text-[#68759C]" :width="16" :height="16"><icon-info /></icon-base>
												</Tooltip>
											</div>
										</div>

										<div class="mt-1 text-[13px] text-[#68759C]">Please select an option.</div>

										<div v-if="enableAdsOnStream" class="mt-3 ml-1 flex flex-col gap-2 text-[13px] text-[#D1DBF0]">
											<FormRadio name="adFrequency" v-model="adFrequency" inputValue="conservative"
												label="Conservative (few ads)" />
											<FormRadio name="adFrequency" v-model="adFrequency" inputValue="balanced" label="Balanced" />
											<FormRadio name="adFrequency" v-model="adFrequency" inputValue="aggressive"
												label="Aggressive (more ads)" />
										</div>
									</div>

									<div v-if="canConfigureMetadata('game')" class="field-container mt-4">
										<FormInput label="Category" v-model="platform.configurableMetadata.game" @input="onChangeGameName"
											placeholder="Enter a category or game" class="mb-2" />

										<drop-down-menu id="twitch-game-dropdown" type="flat" class="region-dropdown w-full mb-2"
											listClasses="dropdown-menu_md test w-full mt-0" size="md" customClasses="twitch-dropdown-toggle"
											ref="twitch-dropdown">
											<template #menu-items>
												<drop-down-menu-item v-for="category in twitchCategories" :key="category.id"
													@click="onSelectTwitchCategory(category)" customClasses="block page-item">
													<div class="main text-s-m text-dark-9 text-500">
														{{ category.name }}
													</div>
												</drop-down-menu-item>
											</template>
										</drop-down-menu>

									</div>

									<div v-if="hasMetaProp('persistent')" class="field-container mt-4">
										<div class="label">
											{{ getMetadataPropValue('persistent', true).label }}
										</div>

										<i v-if="platform.configurableMetadata.persistent" class="fas fa-toggle-on toggle-control enabled"
											@click="setMetadataPropValue('persistent', false)" />

										<i v-else class="fas fa-toggle-off toggle-control"
											@click="setMetadataPropValue('persistent', true)" />
									</div>
								</div>
								<form-button class="w-full justify-center" disabled v-if="processing">
									<Spinner text="Creating..." classes="text-dark-8" spinner-color="var(--c-dark-8)"
										spinner-size="15px" />
								</form-button>
								<div v-if="!processing && (!serviceLinkProcessing || configRequired)">
									<form-button v-if="canSave" class="w-full justify-center mb-2" @click="onSavePlatform"
										:disabled="!isRtmpPassValid || isYoutubeTitleMissing || (customPlatform && customMode === 'SRT' && !srtPidsValid) || ((customSrtPlatformErrors.host.length + customSrtPlatformErrors.latency.length + customSrtPlatformErrors.port.length + customSrtPlatformErrors.server.length + customSrtPlatformErrors.streamId.length + customSrtPlatformErrors.streamKey.length + customSrtPlatformErrors.passphrase.length) > 0)">
										Add destination
									</form-button>
									<form-button type="secondary" class="w-full justify-center" @click="unselectPlatform">
										Cancel
									</form-button>
								</div>
							</div>
						</div>
					</div>
				</div>
				</Transition>
			</section>
		</Teleport>

		<alert-modal
			:description="`Please add Castr application to your Facebook Group '${selectedSubChannel && selectedSubChannel.name}' to connect with Castr`"
			modal-id="modal-facebook-groups-guide"
			message="Please add Castr app to your Facebook Group (Settings -> Manage Advanced Settings -> Apps) or check your permission to stream to Group on Facebook. If you still encounter a problem, please contact our support team."
			ok-text="Close" ok-href="https://web.facebook.com/help/261149227954100?_rdc=1&_rdr" />

		<confirm-modal modal-id="modal-custom-server-premium-feat" z-index="z-[1040]"
			message="This feature is only available in our paid subscriptions" ok-text="Upgrade now" cancel-text="No thanks"
			@modal-confirm="navigatePaymentsPage" />
	</div>
</template>

<script>
import { showModal, hideModal } from '@/Utils/modalBus'
import ModalBox from './ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import _ from 'lodash';
import { v4 } from "uuid"
import Spinner from "@/components/ui/Spinner.vue";
import StreamService from '@/services/StreamService';
import IntegrationService from '@/services/IntegrationService';
import DestinationService from '@/services/DestinationService';
import Badge from '@/components/Badge.vue';
import AlertModal from '@/components/modals/AlertModal.vue';
import Alert from '@/components/Atoms/Alert.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import FormButton from '../../components/Atoms/FormButton.vue';
import TabGroup from '../../components/Atoms/TabGroup.vue';
import TabItem from '../../components/Atoms/TabItem.vue';
import FormInput from '../../components/Atoms/FormInput.vue';
import FloatingInput from '@/components/ui/FloatingInput.vue';
import SrtPidMapping from '@/components/ui/SrtPidMapping.vue';
import { srtPassphraseError, streamIngestKind } from '@/Utils/srtPushTarget';
import FormCheckbox from '../../components/Atoms/FormCheckbox.vue';
import FormSwitch from '../../components/Atoms/FormSwitch.vue';
import FormRadio from '../../components/Atoms/FormRadio.vue';
import IconBase from '@/components/icon/IconBase.vue'
import IconClose from '@/components/icon/IconClose.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconBucket from '@/components/icon/IconBucket.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import AddCastrStream2 from "@/components/ui/AddCastrStream2.vue";
import Tooltip from '@/components/Atoms/Tooltip.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import IconAlertTriangle from '@/components/icon/IconAlertTriangle.vue';
import ProjectDot from '@/components/project/ProjectDot.vue';
import IconChevronLeft from '@/components/icon/IconChevronLeft.vue';
import utils from '@/utils';
import FormText from '@/components/Atoms/FormText.vue';
import { coloredPlatformIcon } from '@/Utils/platformIcon';
import IconFacebookPage from '@/components/icon/IconFacebookPage.vue';
import IconChevronRightStroke from '@/components/icon/IconChevronRightStroke.vue';
import IconSettings from '@/components/icon/IconSettings.vue';
import IconCaretDown from '@/components/icon/IconCaretDown.vue';
import IconInfo from '@/components/icon/IconInfo.vue';
import { track, EVENTS } from '@/lib/tracking';

export default {
	name: 'AddPlatformModal',
	mixins: [legacyModalBridgeMixin],
	legacyModalId: 'modal-add-platform',
	components: {
		ModalBox,
		Alert,
		AlertModal,
		ConfirmModal,
		Spinner,
		FormButton,
		TabGroup,
		TabItem,
		FormInput,
		FloatingInput,
		SrtPidMapping,
		FormCheckbox,
		FormSwitch,
		FormRadio,
		IconClose,
		IconBase,
		IconSearch,
		IconBucket,
		DropDownMenu,
		DropDownMenuItem,
		AddCastrStream2,
		Tooltip,
		IconChevronDown,
		IconAlertTriangle,
		ProjectDot,
		IconChevronLeft,
		FormText,
		Badge,
		IconFacebookPage,
		IconChevronRightStroke,
		IconSettings,
		IconCaretDown,
		IconInfo,
	},
	props: {
		stream: {
			type: Object,
			required: true,
			validator(v) {
				return Object.prototype.hasOwnProperty.call(v, '_id');
			}
		},
		selectedGroup: {
			type: String,
			required: true,
		},
		platformDeleted: Boolean,
		platformUsageData: Object
	},
	data() {
		return {
			// processingSec: 40,
			selectedPrivacyStatus: 'public',
			enableAdsOnStream: false,
			adFrequency: 'balanced',
			oauthProcessing: false,
			chosenServer: null,
			stage: 0,
			processing: false,
			error: null,
			serviceLinkError: false,
			serviceLinkConflict: false,
			platformServerInputMode: null,
			configRequired: false,
			linkedServiceCreds: null,
			serviceLinkProcessing: false,
			platform: { enabled: false, configurableMetadata: {} },
			selectedSubChannel: null,
			customPlatform: false,
			internalWorkflow: false,
			addMode: 'new',
			savedDeleteConfirmId: null,
			creatingSaved: false,
			savingNewSaved: false,
			newSaved: { name: '', server: '', key: '' },
			justCreatedId: null,
			renamingId: null,
			editValues: { name: '', server: '', key: '' },
			savingRename: false,
			// platformTemplates: Platforms,
			platformTemplates: [],
			searchQuery: '',
			platformListProcessing: false,
			serviceResponseRecieved: false,
			formErrors: { server: false, streamKey: false, host: false, port: false, streamId: false },
			oauthConnectId: null,
			isAuthRequired: false,
			rtmpAuthUsername: '',
			rtmpAuthPassword: '',
			streamCreateDropdownActive: false,
			maxLengthTitle: 1000,
			maxLengthDescription: 5000,
			showTitleLimitTracking: false,
			isSchedulerInfiniteLoop: false,
			platformUsage: null,
			usageLoaded: false,
			customMode: 'RTMP',
			customSrtPlatform: {
				server: '',
				host: '',
				port: '',
				streamId: '',
				latency: 120,
				passphrase: '',
				pids: '',
				pmtPid: ''
			},
			srtPidsValid: true,
			customSrtPlatformErrors: {
				server: '',
				host: '',
				port: '',
				streamId: '',
				streamKey: '',
				latency: '',
				host: '',
				passphrase: ''
			},
			platformDisable: false,
			twitchCategories: [],
			isRtmpPassValid: true,
		};
	},
	computed: {
		user() {
			return this.$store.state.User;
		},
		srtIngestKind() {
			return streamIngestKind(this.stream);
		},
		savedDestinations() {
			return this.$store.getters['Destination/destinations'] || [];
		},
		// Linked (active on this stream) first, then the rest — with the just-created
		// destination pinned to the very top so it's easy to find in a long list.
		orderedSavedDestinations() {
			const linked = [];
			const unlinked = [];
			this.savedDestinations.forEach(d => (this.isDestinationLinked(d) ? linked : unlinked).push(d));
			const ordered = [...linked, ...unlinked];
			if (this.justCreatedId) {
				const idx = ordered.findIndex(d => d._id === this.justCreatedId);
				if (idx > 0) ordered.unshift(ordered.splice(idx, 1)[0]);
			}
			return ordered;
		},
		normalizedSearch() {
			return (this.searchQuery || '').trim().toLowerCase();
		},
		isYoutubeTitleMissing() {
			if (!this.platform || this.platform.name !== 'youtube events') return false;
			// the title field only renders in the OAuth-linked metadata form, never in server/key mode
			if (!this.canConfigureMetadata()) return false;
			const title = this.platform.configurableMetadata && this.platform.configurableMetadata.title;
			return !(_.isString(title) && title.trim().length > 0);
		},
		popularPlatformNames() {
			return ['facebook live', 'youtube events', 'twitch', 'tiktok', 'instagram', 'twitter'];
		},
		defunctPlatformKeys() {
			// discontinued services hidden from the picker (existing destinations are untouched)
			return ['ustream', 'livestream', 'nimotv', 'phonelivestreaming'];
		},
		visiblePlatforms() {
			const defunct = this.defunctPlatformKeys;
			return (this.platformTemplates || []).filter(p => {
				if (p.name === 'youtube') return false;
				const key = (p.name || '').toLowerCase().replace(/\s+/g, '');
				return !defunct.includes(key);
			});
		},
		platformLabel() {
			return (p) => (p.name === 'twitter' ? 'x (twitter)' : (p.name || ''));
		},
		filteredPlatforms() {
			const q = this.normalizedSearch;
			if (!q) return this.visiblePlatforms;
			return this.visiblePlatforms.filter(p =>
				this.platformLabel(p).toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q)
			);
		},
		popularPlatforms() {
			if (this.normalizedSearch) return [];
			const order = this.popularPlatformNames;
			return this.visiblePlatforms
				.filter(p => order.includes(p.name))
				.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
		},
		otherPlatforms() {
			if (this.normalizedSearch) return this.filteredPlatforms;
			return this.visiblePlatforms.filter(p => !this.popularPlatformNames.includes(p.name));
		},
		orderedPlatforms() {
			if (this.normalizedSearch) return this.filteredPlatforms;
			return [...this.popularPlatforms, ...this.otherPlatforms];
		},
		customEntry() {
			return { name: 'custom', isCustom: true };
		},
		destinationUsage() {
			const used = Number(this.platformUsage?.usedDestination || 0);
			const total = Number(this.platformUsage?.totalDestination || 0);
			const remaining = Math.max(total - used, 0);
			const percent = total > 0 ? Math.min(Math.round((used / total) * 100), 100) : 0;
			return {
				used,
				total,
				remaining,
				percent,
				// Destinations carried by streams in the workspace being viewed.
				// Older servers do not send it; `null` then means "say nothing"
				// rather than "none".
				inWorkspace: Number.isFinite(Number(this.platformUsage?.workspaceDestination))
					? Number(this.platformUsage.workspaceDestination)
					: null,
				isMax: total > 0 && used >= total,
				nearLimit: total > 0 && used / total >= 0.8,
			};
		},
		activeProjectColor() {
			return this.$store.getters['Projects/activeProject']?.color || '#3b82f6';
		},
		// With one workspace the split says nothing the plan line has not: every
		// destination on the account is in the workspace you are standing in.
		showWorkspaceDestinations() {
			return this.destinationUsage.inWorkspace !== null
				&& this.$store.getters['Projects/hasMultipleProjects'];
		},
		gridPlatforms() {
			if (this.normalizedSearch) {
				return this.customMatchesSearch ? [this.customEntry, ...this.filteredPlatforms] : this.filteredPlatforms;
			}
			return [this.customEntry, ...this.popularPlatforms, ...this.otherPlatforms];
		},
		customMatchesSearch() {
			const q = this.normalizedSearch;
			return !q || 'custom'.includes(q);
		},
		noPlatformResults() {
			return !!this.normalizedSearch && this.filteredPlatforms.length === 0 && !this.customMatchesSearch;
		},
		canSave() {
			const validStage = this.stage > 0;
			return validStage;
		},
		hasPremiumSubscription() {
			const userSubs = this.$store.state.User.subscriptions.map(sub => _.assign({ category: 'restream' }, sub));
			const subscription = userSubs.find(sub => sub.category && sub.category.includes(this.stream.type));
			let isPremiumSubs = false;
			userSubs.forEach((item) => {
				if (item.category.includes(this.stream.type) && item.package && item.package.definition.allowLinkingFbPage !== false) {
					isPremiumSubs = true;
				}
			})
			// And the current sub is not Simulcast yearly & Broadcast trial 7 days (ID 253, 171, 250)
			// ref: https://docs.google.com/spreadsheets/d/1smxssUcDSyK-AM1pzIFHxa5vgXNsnMDAaMbWqrRW6Pc
			return isPremiumSubs && subscription.package._id !== '5fffc31103afd67aa9831cc6' && subscription.package._id !== '5ccc3a3c588ccab4bdd556e7' &&
				subscription.package._id !== '5fffc14a03afd67aa9831cc3' && subscription.package._id !== '5ccc2ee6588ccab4bdd556df';
		},
		subscriptions() {
			return this.$store.state.User.subscriptions.map(sub => _.assign({ category: 'restream' }, sub));
		},
		isBroadcastSubscription() {
			// Check if the user's package is 'Simulcast monthly/yearly'
			const simulcastMonthlyId = '5fffc14a03afd67aa9831cc3'
			const simulcastYearlyId = '5fffc31103afd67aa9831cc6'
			const broadcast7DaysTrial = '5ccc3a3c588ccab4bdd556e7'
			let isBroadcastSubscription = false
			this.$store.state.User.subscriptions.forEach(sub => {
				if (sub.enabled === true && (
					sub.package._id === simulcastMonthlyId
					|| sub.package._id === simulcastYearlyId
					|| sub.package._id === broadcast7DaysTrial
				) && sub.package.category === this.stream.type) {
					isBroadcastSubscription = true
				}
			});
			return isBroadcastSubscription
		}
	},
	watch: {
		'customSrtPlatform.passphrase'() {
			this.checkSrtPassphrase();
		},
		isAuthRequired: function (isRequired) {
			if (!isRequired) {
				this.platform.server = this.platform.server.replace(
					/:\/\/.*?:.*?@/,
					'://'
				);
			} else {
				this.rtmpAuthUsername = '';
				this.rtmpAuthPassword = '';
			}
		},
		platformDeleted: function () {
			this.fetchPlatformUsage();
		},
		platformUsageData: function () {
			this.platformUsage = this.platformUsageData;
		},
		rtmpAuthUsername: function (val) {
			if (this.platform.server) {
				this.platform.server = this.platform.server.replace(
					/:\/\/.*?:.*?@/,
					'://'
				);
				if (val && this.rtmpAuthPassword) {
					this.platform.server = this.platform.server.replace(
						'://',
						'://' +
						encodeURIComponent(val) +
						':' +
						encodeURIComponent(this.rtmpAuthPassword) +
						'@'
					);
				}
			}
		},
		rtmpAuthPassword: function (val) {
			if (this.platform.server) {
				this.platform.server = this.platform.server.replace(
					/:\/\/.*?:.*?@/,
					'://'
				);
				if (this.rtmpAuthUsername && val) {
					this.platform.server = this.platform.server.replace(
						'://',
						'://' +
						encodeURIComponent(this.rtmpAuthUsername) +
						':' +
						encodeURIComponent(val) +
						'@'
					);
				}
			}
		}
	},
	async mounted() {

		this.initPlatformTemplates();
	},
	methods: {
		coloredIcon(plat) {
			return coloredPlatformIcon(plat);
		},
		onLegacyModalShow() {
			this.onInit();
		},
		onLegacyModalHide() {
			this.onDismiss();
		},
		onPrivacySettingsChanged(status, platform) {
			this.selectedPrivacyStatus = status;
			platform.configurableMetadata.privacyStatus = status;
			if (_.isArray(this.$refs.youtubePrivacyDropdownInDialog)) {
				this.$refs.youtubePrivacyDropdownInDialog.forEach(x => x.toggleDropdown())
			} else {
				this.$refs.youtubePrivacyDropdownInDialog.toggleDropdown();
			}
		},
		selectInternalWorkflow() {
			this.clearErrors();
			this.stage = 0;

			setTimeout(() => {
				this.internalWorkflow = true
				this.stage = 1;
			}, 250);

		},
		onChangeGameName: _.debounce(async function (name) {
			if (!this.linkedServiceCreds) return;
			this.twitchCategories = await IntegrationService.getTwitchCategories(this.linkedServiceCreds, name);
			if (this.twitchCategories?.length) this.$refs['twitch-dropdown'].show();
		}, 250),
		onSelectTwitchCategory(category) {
			if (!this.linkedServiceCreds) return;
			this.platform.configurableMetadata.game_id = category.id;
			this.platform.configurableMetadata.game = category.name;
			this.hideLegacyModal();
			this.$forceUpdate();
		},
		onCustomModeChanged(mode) {
			this.customMode = mode
		},
		onInit() {
			// Load saved destinations (force refresh so linkedStreams is accurate)
			// for the "From saved" section.
			this.$store.dispatch('Destination/getDestinations', true);
			// Seed usage from what the parent already has (avoids a blank → pop-in),
			// then refresh in the background.
			if (this.platformUsageData) {
				this.platformUsage = this.platformUsageData;
				this.usageLoaded = true;
			} else {
				this.usageLoaded = false;
			}
			this.fetchPlatformUsage();
		},
		isDestinationLinked(destination) {
			return destination?.linkedStreams?.some(s => s._id === this.stream._id) || false;
		},
		isDestinationDisabled(destination) {
			return !this.isDestinationLinked(destination) && this.destinationUsage.isMax;
		},
		openCreateSaved() {
			this.creatingSaved = true;
			this.newSaved = { name: '', server: '', key: '' };
		},
		cancelCreateSaved() {
			this.creatingSaved = false;
			this.newSaved = { name: '', server: '', key: '' };
		},
		async saveCreateSaved() {
			const name = (this.newSaved.name || '').trim();
			const server = (this.newSaved.server || '').trim();
			const key = (this.newSaved.key || '').trim();
			if (!name || !server) {
				this.$notify({ group: 'error', text: 'Name and server are required' });
				return;
			}
			this.savingNewSaved = true;
			try {
				const created = await DestinationService.createDestination({ name, template: 'custom', server, key, enabled: false });
				if (created && created._id) {
					await DestinationService.addDestinationToStream(created._id, this.stream._id);
					this.justCreatedId = created._id;
				}
				await this.$store.dispatch('Destination/getDestinations', true);
				this.$store.commit('PlatformUsage/CLEAR_USAGE', this.stream.type);
				this.fetchPlatformUsage();
				this.$emit('destinations-changed');
				this.$notify({ group: 'success', text: 'Destination created' + (created && created._id ? ' & linked' : '') });
				this.creatingSaved = false;
				this.newSaved = { name: '', server: '', key: '' };
				// Bring the newly added destination into view at the top of the list.
				this.$nextTick(() => { if (this.$refs.savedListEl) this.$refs.savedListEl.scrollTop = 0; });
			} catch (err) {
				this.$notify({ group: 'error', title: 'Create failed', text: err?.message || 'Could not create destination.' });
			} finally {
				this.savingNewSaved = false;
			}
		},
		startRename(destination) {
			this.savedDeleteConfirmId = null;
			this.renamingId = destination._id;
			this.editValues = {
				name: destination.name || '',
				server: destination.server || '',
				key: destination.key || ''
			};
		},
		cancelRename() {
			this.renamingId = null;
			this.editValues = { name: '', server: '', key: '' };
		},
		async saveRename(destination) {
			const name = (this.editValues.name || '').trim();
			if (!name) {
				this.$notify({ group: 'error', text: 'Name is required' });
				return;
			}
			// Only custom (RTMP/SRT) destinations expose the server/key fields;
			// OAuth-linked platforms keep their existing ingest details untouched.
			const isCustom = destination.template === 'custom';
			const server = isCustom ? (this.editValues.server || '').trim() : destination.server;
			const key = isCustom ? (this.editValues.key || '').trim() : destination.key;
			if (isCustom && !server) {
				this.$notify({ group: 'error', text: 'Server URL is required' });
				return;
			}
			const unchanged = name === (destination.name || '')
				&& (!isCustom || (server === (destination.server || '') && key === (destination.key || '')));
			if (unchanged) {
				this.cancelRename();
				return;
			}
			this.savingRename = true;
			try {
				await DestinationService.updateDestination(destination._id, name, server, key, destination.port, destination.latency, destination.passphrase);
			} catch (err) {
				this.savingRename = false;
				this.$notify({ group: 'error', title: 'Update failed', text: err?.message || 'Could not update destination.' });
				return;
			}
			// Save succeeded — exit edit mode and re-enable immediately so the UI
			// never appears stuck while the (potentially slow) refresh runs.
			this.savingRename = false;
			this.cancelRename();
			this.$notify({ group: 'success', text: 'Destination updated' });
			try {
				await this.$store.dispatch('Destination/getDestinations', true);
				this.$emit('destinations-changed');
			} catch (e) { /* refresh is best-effort */ }
		},
		async confirmDeleteSaved(destination) {
			try {
				await DestinationService.deleteDestination(destination._id);
				this.$notify({ group: 'success', text: 'Destination deleted' });
			} catch (err) {
				this.$notify({ group: 'error', title: 'Delete failed', text: err?.message || 'Could not delete destination.' });
			} finally {
				this.savedDeleteConfirmId = null;
				await this.$store.dispatch('Destination/getDestinations', true);
				this.$store.commit('PlatformUsage/CLEAR_USAGE', this.stream.type);
				this.fetchPlatformUsage();
				this.$emit('destinations-changed');
				if (!this.savedDestinations.length) this.unselectPlatform();
			}
		},
		async toggleDestinationLink(destination) {
			const wasLinked = this.isDestinationLinked(destination);
			try {
				if (wasLinked) {
					await DestinationService.removeDestinationFromStream(destination._id, this.stream._id);
					this.$notify({ group: 'success', text: 'Destination removed' });
				} else {
					await DestinationService.addDestinationToStream(destination._id, this.stream._id);
					this.$notify({ group: 'success', text: 'Destination added' });
				}
			} catch (err) {
				this.$notify({ group: 'error', title: 'Destination update failed', text: err?.message || 'Could not update destination for this stream.' });
			} finally {
				await this.$store.dispatch('Destination/getDestinations', true);
				this.$store.commit('PlatformUsage/CLEAR_USAGE', this.stream.type);
				this.fetchPlatformUsage();
				this.$emit('destinations-changed');
			}
		},
		async initPlatformTemplates() {
			this.platformListProcessing = true;
			// Use Vuex store to prevent duplicate platformList.json fetches
			this.platformTemplates = await this.$store.dispatch('Destination/getPlatforms');
			this.platformListProcessing = false;
		},
		getStreamtype() {
			const category = this.stream.type;
			let CategoryName = null;
			if (category == 'live') {
				CategoryName = 'Live Streams'
			} else if (category == 'scheduled') {
				CategoryName = 'Pre-recorded Streams'
			} else if (category == 'restream') {
				CategoryName = 'Multistreams'
			}
			return CategoryName;
		},
		navigatePaymentsPage() {
			const category = this.stream.type;
			window.location.href = `/app/subscribe?category=${category}`;
		},
		async fetchPlatformUsage() {
			// Use Vuex store for platform usage (prevents duplicates)
			try {
				const platformUsage = await this.$store.dispatch('PlatformUsage/getPlatformUsage', this.stream.type);
				if (platformUsage) this.platformUsage = platformUsage;
			} finally {
				this.usageLoaded = true;
			}
		},
		onFacebookPopup(e) {
			e.preventDefault();
			showModal('modal-facebook-integration-prompt'
			);
		},
		stepBack() {
			this.stage--;

			this.clearErrors();
			this.resetPlatform();
		},
		focusServerKeySegment(event) {
			const soruceElement = event.target || event.srcElement;
			if (soruceElement instanceof HTMLInputElement) return;

			this.$refs.segment[0].focus();
		},
		computePlatformServerFromSegments() {
			const { platform } = this;
			console.log(platform);
			const { serverKeySegments, serverKeySegmentValues } = platform;
			const inputSegments = _.filter(serverKeySegments, { input: true });
			const validSegmentValues = _.compact(serverKeySegmentValues);

			if (_.size(inputSegments) !== _.size(validSegmentValues)) {
				this.platform.server = null;
				return;
			}

			let serverAddr = '';
			_.each(platform.serverKeySegments, (segment, index) => {
				if (segment.exclude) return;

				if (segment.input) {
					serverAddr += serverKeySegmentValues[index];
				} else {
					serverAddr += segment.text || segment;
				}
			});

			this.platform.server = serverAddr;
		},
		async selectPlatform(platform) {
			this.clearErrors();

			if (this.platform.name || this.customPlatform) {
				// Swap content in place; keep the panel open instead of closing and reopening
				this.resetPlatformSelection();
			}
			this.serviceLinkProcessing = false;
			this.selectedSubChannel = null;
			this.serviceResponseRecieved = false;
			this.customPlatform = !platform;
			this.platform = _.assign({}, this.platform, _.cloneDeep(platform));
			this.platformServerInputMode = this.platform.serverKeySegments && Object.keys(this.platform.serverKeySegments).length
				? 'chunks'
				: this.platform.servers
					? 'select'
					: 'input';

			if (this.canConfigureMetadata()) {
				this.platform.configurableMetadata = {
					...this.platform.configurableMetadata,
					title: this.getMetadataPropValue('title'),
					description: this.getMetadataPropValue('description'),
					persistent: false
				};
			}

			let preselectedServer = this.platform.servers && this.platform.servers[0];
			if (preselectedServer) {
				preselectedServer = preselectedServer.value || preselectedServer;
				this.platform.server = preselectedServer;
			}

			let canConfiguredOAuth = this.platform.canLinkService;

			// Hardcode LinkedIn to always use Connect to... method
			if (this.platform.name === 'LinkedIn' && this.user && this.user._id === '6523a212c7eacf62b13abfc3') {
				canConfiguredOAuth = true;
				this.configRequired = false;
				this.platform.customServer = false;
				this.platform.serviceAuthorization = {
					enabled: true,
					metadata: {
						fields: [
							// {
							// 	key: "title",
							// 	required: true
							// },
							{
								key: "description",
								value: "Multistreaming with Castr"
							}
						]
					},
					// subChannelRequired: true,
					// subChannelBasedMetadata: true,
					// subChannelFieldLabel: "Select Event"
				};
			} else if (canConfiguredOAuth && this.platform.oauthCheckRequired) {
				canConfiguredOAuth = false;
				const usersubs = this.subscriptions;
				const basesub = _.find(usersubs, { category: this.stream.type });
				if (basesub) {
					let oauthIntegrationsRules = basesub.oauthIntegrations;
					// if (!oauthIntegrationsRules && basesub.definitionOverride) {
					//   oauthIntegrationsRules = basesub.definitionOverride.oauthIntegrations
					// }

					if (!oauthIntegrationsRules) {
						// extract from sub def overrides
						if (basesub.definitionOverride) {
							oauthIntegrationsRules = basesub.definitionOverride.oauthIntegrations;
						}

						// extract from sub pack def
						if (!oauthIntegrationsRules) {
							oauthIntegrationsRules = basesub.package.definition.oauthIntegrations;
						}
					}

					const canlinkService = _.find(oauthIntegrationsRules, { template: this.platform.slug || this.platform.name, enabled: true });
					canConfiguredOAuth = !!canlinkService;
				}

				// We allow scheduled stream with datetime mode connect to Facebook via API
				if (this.stream.type === 'scheduled') {
					canConfiguredOAuth = true;
					const schedulerConfig = await StreamService.getStreamScheduleSettings(this.stream._id) || {};
					if (schedulerConfig.mode === 'loop') {
						this.isSchedulerInfiniteLoop = true;
					} else this.isSchedulerInfiniteLoop = false;
				}
			}

			// this.configRequired = !this.platform.canLinkService
			this.configRequired = !canConfiguredOAuth;

			// move to next step
			this.stage = 1;
		},
		resetPlatformSelection() {
			this.platform = {
				enabled: false,
				configurableMetadata: {}
			};

			this.rtmpAuthUsername = '';
			this.rtmpAuthPassword = '';
			this.customPlatform = false;
			this.configRequired = false;
			this.linkedServiceCreds = null;
			this.selectedSubChannel = null;
			this.internalWorkflow = false;
			this.savedDeleteConfirmId = null;
		},
		unselectPlatform() {
			this.resetPlatformSelection();
			this.stage = 0;
		},
		checkSrtPassphrase() {
			const passphrase = this.customSrtPlatform.passphrase || '';
			if (/[\]\[\^$&]/.test(passphrase)) {
				this.customSrtPlatformErrors.passphrase = 'Passphrase cannot contain ], [, ^, $, or &'
			} else {
				this.customSrtPlatformErrors.passphrase = srtPassphraseError(passphrase) || ''
			}
		},
		onInputChange(prop) {
			this.formErrors[prop] = false;


			if (prop === 'port' && this.customSrtPlatform.port) {
				const portNum = parseInt(this.customSrtPlatform.port)
				if (!portNum || /\D/.test(this.customSrtPlatform.port)) {
					this.customSrtPlatformErrors.port = 'Port must be a number'
					// replace value
					this.customSrtPlatform.port = ''
				}
				else if (portNum > 65535) {
					this.customSrtPlatformErrors.port = 'Port must be equal to or below 65535'
				} else {
					this.customSrtPlatformErrors.port = ''
				}
			} else {
				this.customSrtPlatformErrors.port = ''
			}

			if (prop === 'latency' && this.customSrtPlatform.latency) {
				const portNum = parseInt(this.customSrtPlatform.latency)
				if (!portNum || /\D/.test(this.customSrtPlatform.latency)) {
					this.customSrtPlatformErrors.latency = 'Latency must be a number'
					// replace value
					this.customSrtPlatform.latency = ''
				} else {
					this.customSrtPlatformErrors.latency = ''
				}
			} else {
				this.customSrtPlatformErrors.latency = ''
			}

			// remove unnecessary key chars
			if (prop === 'streamKey') {
				const regexp = new RegExp('^(?!.*@.*@)(?:[A-Za-z0-9\\-._~!$&\'()*+,;=:@%?#/]|%[0-9A-Fa-f]{2})+$', 'i');
				if (/\^/.test(this.platform.streamKey) || !regexp.test(this.platform.streamKey)) {
					this.customSrtPlatformErrors.streamKey = 'invalid streaming key'
					this.error = { message: 'Streaming key is invalid' }

					// replace value
					// this.customSrtPlatform.host = ''
				} else {
					this.customSrtPlatformErrors.streamKey = ''
				}
			}
			if (prop === 'streamKey') {
				setTimeout(() => {
					this.platform[prop] = utils.resolveStreamKey(
						this.platform[prop]
					);
				});
			}
			if (prop === 'host') {
				this.customSrtPlatform.host = this.customSrtPlatform.host.replace(/\s/g, '');
				if (/\^/.test(this.customSrtPlatform.host)) {
					this.customSrtPlatformErrors.host = 'invalid host format'
					// replace value
					// this.customSrtPlatform.host = ''
				} else {
					this.customSrtPlatformErrors.host = ''
				}
			}
			if (prop === 'streamId') {
				this.customSrtPlatform.streamId = this.customSrtPlatform.streamId.replace(/\s/g, '');
				if (/\^/.test(this.customSrtPlatform.streamId)) {
					this.customSrtPlatformErrors.streamId = 'invalid streamId format'
					// replace value
					// this.customSrtPlatform.host = ''
				} else {
					this.customSrtPlatformErrors.streamId = ''
				}
			}
			if (prop === 'passphrase') {
				this.checkSrtPassphrase()
			}
			// setTimeout(() => {
			// 	if (
			// 		this.platform.server &&
			//    !this.platform.server.match(/^[a-z]+:\/\//i)
			// 	) {
			// 		this.platform.server = 'rtmp://' + this.platform.server;
			// 	}
			// });
			if (prop === 'server') {
				this.platform.server = this.platform?.server?.replace(/\s/g, '');
				if (/\^/.test(this.platform.server)) {
					this.customSrtPlatformErrors.server = 'invalid destination ingest address'
					// replace value
					// this.customSrtPlatform.host = ''
				} else {
					this.customSrtPlatformErrors.server = ''
				}
			}
			setTimeout(() => {
				if (prop === 'server' && this.platform.server) {
					let userAuthMatch;

					if (
						(userAuthMatch = this.platform.server.match(
							/:\/\/(.*?):(.*?)@/
						))
					) {
						this.isAuthRequired = true;

						setTimeout(() => {
							this.rtmpAuthUsername = decodeURIComponent(
								userAuthMatch[1]
							);
							this.rtmpAuthPassword = decodeURIComponent(
								userAuthMatch[2]
							);
						}, 100);
					}
				}
			}, 100);
		},
		selectServer(server) {
			this.chosenServer = server.text
			this.platform.server = server.value;

		},
		shouldConfigureServer() {
			const haveConfigured =
				this.platform.server && this.platformServerInputMode === 'auto';


			const result = !haveConfigured &&
				(this.customPlatform || this.platform.customServer || this.hasMultiplePlatformServers(this.platform));
			if (result) {
				if (this.platform.name === 'facebook live' && !this.platform.server) {
					this.platform.server = 'rtmps://live-api-s.facebook.com:443/rtmp'
				}
				if (this.platform.name === 'youtube events' && !this.platform.server) {
					this.platform.server = 'rtmp://a.rtmp.youtube.com/live2'
				}
				if ((this.platform.name || '').toLowerCase() === 'vimm.tv' && !this.platform.server) {
					this.platform.server = 'rtmp://rtmp.livepeer.com/live'
				}
			}
			return result;
		},
		checkServerInputMode(matchInputMode) {
			return (
				this.platformServerInputMode &&
				this.platformServerInputMode === matchInputMode
			);
		},
		shouldShowServiceLinkingPrompt() {
			return !this.configRequired;
			// return this.platform.canLinkService && !this.configRequired
			// return this.platform.canLinkService;
			// const bool = this.platform.canLinkService && !this.platform.ignoreServiceLinking
			// console.log('shouldShowServiceLinkingPrompt', bool)
			// return bool
		},
		ignoreServiceLinking() {
			if (this.platform.customServerPremiumFeat) {
				const baseSub = this.subscriptions.find(sub => sub.category && sub.category.includes(this.stream.type));
				const isTrialSub = /trial/gi.test(baseSub.package.name || '');
				if (isTrialSub) {
					showModal('modal-custom-server-premium-feat'
					);
					return;
				}
			}

			this.configRequired = true;
			this.platform.customServer = true;
			// this.platform.canLinkService = false;
			// this.platform.ignoreServiceLinking=true
			// console.log(this.platform);
		},
		hasMultiplePlatformServers(platform) {
			return (
				_.size(platform.servers) > 1 || _.size(platform.serverKeySegments)
			);
		},
		getServiceOAuthUrl() {
			if (this.isSchedulerInfiniteLoop) return;
			const oauthFactoryName =
				this.platform.oauthSlug || this.platform.slug || this.platform.name;
			const pathname = `integrations/${oauthFactoryName}/oauth`;
			const connectId = v4()
			if (!this.oauthConnectId) this.oauthConnectId = connectId;

			const searchParams = `redirect=1&user=${this.user._id || this.user.id}&stream=${this.stream._id
				}&connectId=${this.oauthConnectId}`;
			let routeUrl = `${process.env.VUE_APP_APP_API_BASE}/${pathname}?${searchParams}`;
			console.log(routeUrl);
			if (window.todesktop && (oauthFactoryName == 'facebook')) {
				window.todesktop.contents.openUrlInBrowser(routeUrl);
			} else {
				window.open(routeUrl, '_blank');
			}

		},
		cancelServiceLinking() {
			this.serviceLinkError = false;
			this.serviceLinkConflict = false;
			this.serviceLinkProcessing = false;
		},
		async onServiceAuthURLTap() {
			// Check if there is any available platform to add
			try {
				const isAvailableSlot = await StreamService.addStreamPlatform(this.stream._id, {
					template: this.customPlatform ? 'custom' : this.platform.name,
					server: 'test',
					key: 'test',
					enabled: false,
					preChecking: true
				})
			} catch (error) {
				console.error(error.stack);
				if (error.message === 'Please upgrade your subscription to add more streams and platforms.') {
					return this.error = error;
				}
			}

			this.serviceLinkConflict = false;
			this.serviceLinkProcessing = true;

			// const serviceName = this.platform.name;
			const connectId = this.oauthConnectId;
			// const connectId = '17379bd5-6cbc-4f3e-946c-936fa9aa85fc';
			this.oauthConnectId = null;

			// const ackTag = 'oauth-link'
			// Vue.appEvents.emit('ack.listen', ackTag, connectId)
			// const subkey = [ackTag, connectId].join('.')

			// #DISABLED FOR DEBUG
			// Vue.appEvents.on(subkey, this.onServiceAuthResponse)
			this.oauthProcessing = true;
			this.listenAckEventResponse(connectId, this.onServiceAuthResponse);
			// #ENABLED FOR DEBUG
			// this.onServiceAuthResponse({linkedMeta: "5dc40186ed240b447c1d911b"})
		},
		async listenAckEventResponse(key, callback) {
			if (this.oauthProcessing && !this.serviceLinkProcessing) return;
			const ackRes = await IntegrationService.getAckByKey(key);
			// console.log('ackRes', ackRes)
			if (ackRes && ackRes.success) {
				callback(ackRes.data);
				this.oauthProcessing = false;
			} else {
				setTimeout(() => {
					this.listenAckEventResponse(key, callback);
				}, 4000);
			}
		},
		hasMetaProp(propname) {
			if (
				!this.linkedServiceCreds ||
				!this.platform.serviceAuthorization ||
				!propname
			)
				return;

			const { metadata } = this.platform.serviceAuthorization;

			// let canConfigure = false
			const { fields } = metadata;

			// perform multi key matching `OR`
			if (!_.isArray(propname)) {
				propname = [propname];
			}

			let keyFound = false;
			_.each(propname, key => {
				// check for property existences
				if (keyFound) return;
				const field = _.find(fields, { key });
				keyFound = !!field;
			});

			return keyFound;
		},
		canConfigureMetadata(propname) {
			if (
				!this.linkedServiceCreds ||
				!this.platform.serviceAuthorization ||
				(this.platform.serviceAuthorization &&
					this.platform.serviceAuthorization.subChannelBasedMetadata &&
					!this.platform.subChannel &&
					propname)
			)
				return;

			const { metadata } = this.platform.serviceAuthorization;
			if (propname === undefined) {
				return !!metadata;
			}

			let canConfigure = false;
			const { fields } = metadata;

			// check for property being disabled
			const field = _.find(fields, { key: propname });
			canConfigure = field && !field.disabled;
			return canConfigure;
		},
		getMetadataPropValue(propname, getDefinition) {
			const { fields } = this.platform.serviceAuthorization.metadata;
			const field = _.find(fields, { key: propname });
			return getDefinition ? field : field && field.value;
		},
		setMetadataPropValue(propname, value) {
			this.platform.configurableMetadata[propname] = value;
		},
		showFacebookGroupsGuide() {
			showModal('modal-facebook-groups-guide');
		},
		async setSubChannels(oauthMetaId) {
			this.platform.subChannelList = await IntegrationService.getServiceSubChannels(oauthMetaId);
			if (this.platform.subChannelList.length) {
				if (this.platform.serviceAuthorization && this.platform.serviceAuthorization.autoSelectDefaultSubChannel) {
					this.changeSubChannelSelection(this.platform.subChannelList[0]);
				}
			}
		},
		onSubchannelListToggle(state) {
			this.streamCreateDropdownActive = state;
		},
		isSubChannelDisabled(subChannelItem) {
			let isDisabled = false;
			if (!this.hasPremiumSubscription && /facebook/g.test(this.platform.name)) {
				isDisabled = subChannelItem.id !== 0;
			}
			return isDisabled;
		},
		changeSubChannelSelection(subChannelItem) {
			if (this.isSubChannelDisabled(subChannelItem)) return;

			this.selectedSubChannel = subChannelItem;
			const subchannelSlug = (subChannelItem.type || '-') + '@' + subChannelItem.id;
			this.platform.subChannel = subchannelSlug;
			this.onSubChannelChange();
			this.$refs.subChannelDropdown.hide(true);
		},
		async onSubChannelChange() {
			let selectedSubChannel = this.platform.subChannel;
			if (selectedSubChannel === 'new') return;
			const subChannelId = selectedSubChannel.split('@')[1];
			selectedSubChannel = this.platform.subChannelList.find(({ id }) => id === subChannelId);

			if (this.platform.serviceAuthorization && this.platform.serviceAuthorization.subChannelBasedMetadata) {
				if (selectedSubChannel) {
					if (selectedSubChannel.metadata) {
						const chMeta = selectedSubChannel.metadata;
						this.platform.configurableMetadata = {
							...this.platform.configurableMetadata,
							title: chMeta.title,
							description: chMeta.description
						};
					}
				}
			}
		},
		async onServiceAuthResponse(response) {
			if (this.serviceResponseRecieved) return;
			this.serviceResponseRecieved = true;
			if (!response || response.error) {
				this.serviceLinkError = true;
				return;
			}
			const oauthMetaId = response.linkedMeta;
			// acknowledge UI, authorization was successful
			this.linkedServiceCreds = oauthMetaId;

			// check for linking conflicts
			let linkResult;
			try {
				linkResult = await IntegrationService.checkIntegrationConflict(this.stream._id, oauthMetaId);
			} catch (err) {
				console.log('integration-conflict-check-error', err);
				window.alert('Unable to verify this connection. Please try again.');
			}

			if (!linkResult || linkResult.linked) {
				// conflicts are there
				this.serviceLinkError = true;
				this.serviceLinkConflict = true;
				this.linkedServiceCreds = null;
				return;
			}

			// check if serice supports metadata integration
			const { serviceAuthorization } = this.platform;
			// const { serviceAuthorization = true } = this.platform;
			if (serviceAuthorization && serviceAuthorization.subChannelRequired) {
				await this.setSubChannels(oauthMetaId);
			}

			let metaOverrides = {};
			const ingestConfParams = {};

			if (serviceAuthorization && serviceAuthorization.subChannelBasedMetadata) {
				// this.configRequired = true
				// this.platformServerInputMode = 'auto'
				metaOverrides = {
					title: '',
					description: '',
					game: ''
				};
				ingestConfParams.subChannelBasedMetadata = true;
			}
			console.log('serviceAuthorization', serviceAuthorization);


			const ingest = await this.setServiceIngest(oauthMetaId, ingestConfParams);
			if (!ingest) return;
			let configRequired = false;
			if (ingest.server) {
				this.platformServerInputMode = 'auto';
			} else {
				configRequired = true;
			}

			if (serviceAuthorization && serviceAuthorization.metadata) {
				configRequired = true;
				await this.setupServiceMetadata(oauthMetaId, metaOverrides);
			}

			this.configRequired = configRequired;
			if (configRequired) return;

			this.onSavePlatform();
		},
		async setServiceIngest(oauthMetaId, params) {
			let ingest;
			try {
				ingest = await IntegrationService.getServiceIngest(
					oauthMetaId,
					params
				);
			} catch (e) {
				this.serviceLinkError = true;
				return;
			}

			if (!ingest) {
				this.serviceLinkError = true;
				return;
			}

			if (ingest.streamCreationRequired) {
				this.platform.streamCreationRequired = true;
				ingest._id = oauthMetaId;
				ingest.key = '#streamkey';
				ingest.server = '#serveraddr';
			}

			this.platform.enabled = true;
			this.platform.streamKey = ingest.key;
			this.platform.server = ingest.server;
			this.linkedServiceCreds = ingest._id;

			return ingest;
		},
		async setupServiceMetadata(oauthMetaId, metaOverrides = {}) {
			// get existing metadata details
			try {
				const metaprops = {
					title: this.getMetadataPropValue('title'),
					description: this.getMetadataPropValue('description'),
					game: this.getMetadataPropValue('game')
				};

				for (const prop in metaOverrides) {
					if (Object.prototype.hasOwnProperty.call(metaOverrides, prop)) {
						metaprops[prop] = metaOverrides[prop];
					}
				}

				if (this.platform.streamCreationRequired) {
					this.platform.configurableMetadata = _.assign(
						{},
						this.platform.configurableMetadata,
						{
							title: metaprops.title,
							description: metaprops.description,
							game: metaprops.game,
							persistent: false
						}
					);
				} else {
					let streamMeta = await IntegrationService.getIntegrationMetadata(oauthMetaId);
					streamMeta.persistent = false;

					streamMeta = Object.assign({}, streamMeta, metaOverrides);
					this.platform.configurableMetadata = streamMeta;
				}

				// check for fixed/disabled fields
				['title', 'description', 'game'].forEach(prop => {
					const metaField = this.getMetadataPropValue(prop, true);
					const { disabled } = metaField || {};
					let { value: metavalue } = metaField || {};
					metavalue = prop in metaprops ? metaprops[prop] : metavalue;
					if (disabled) this.platform.configurableMetadata[prop] = metavalue;
				});
			} catch (e) {
				console.log(e);
			}

			// Set up platform title limit for Youtube event
			if (this.platform.name === 'youtube events') {
				if (this.platform.configurableMetadata && _.isString(this.platform.configurableMetadata.title)) {
					this.showTitleLimitTracking = true
				}
				this.maxLengthTitle = 100
			} else {
				this.showTitleLimitTracking = false
				this.maxLengthTitle = 1000
			}
		},
		isServiceAlreadyLinked() {
			// const currentTemplate = this.platform.name
			// const linked = _.find(
			//   this.stream.platforms,
			//   platform =>
			//     platform.template === currentTemplate && platform.linkedServiceCreds
			// )
			// return !!linked;
			return false;
		},
		async onSavePlatform() {
			// this.processingSec = 40
			// let intervalctrl = setInterval(() => {
			//   if (!this.processingSec) {
			//     clearInterval(intervalctrl)
			//     return
			//   }

			//   this.processingSec -= 1
			// }, 1000)
			this.checkPassword()
			this.error = null;

			if (!this.validateForm() && !this.platform.streamCreationRequired || !this.isRtmpPassValid) {
				// window.alert('warning: form not completely filled')
				return;
			}

			if (
				this.linkedServiceCreds &&
				this.platform.serviceAuthorization &&
				this.platform.serviceAuthorization.subChannelRequired &&
				!this.platform.subChannel
			) {
				let channelType = 'sub channel';
				if (this.platform.template === 'youtube') {
					channelType = 'event';
				}

				window.alert(`Please select a ${channelType}.`);
				return;
			}

			this.processing = true;
			let payload;

			if (this.customMode === 'SRT' && this.customPlatform) {

				if (this.customSrtPlatform.host.endsWith('/')) {
					this.customSrtPlatform.host = this.customSrtPlatform.host.replace(/\/$/, '');
				}

				payload = {
					enabled: this.platform.enabled,
					server: this.modifySrtUrl(this.customSrtPlatform.host),
					key: this.customSrtPlatform.streamId,
					template: 'custom',
					mode: 'srt',
					port: this.customSrtPlatform.port,
					latency: this.customSrtPlatform.latency,
					passphrase: this.customSrtPlatform.passphrase,
					pids: this.customSrtPlatform.pids,
					pmtPid: this.customSrtPlatform.pmtPid
				};
			} else {
				// let serverAddr = this.platform.server
				// serverAddr = _.replace(serverAddr, /^rtmps/gi, 'rtmp')
				// serverAddr = _.replace(serverAddr, /\:443/gi, ':80')
				let serverAddr = this.platform.server;
				serverAddr = utils.resolveURL(serverAddr, true);

				let streamKey = this.platform.streamKey;
				streamKey = utils.resolveStreamKey(streamKey);

				payload = {
					enabled: this.platform.enabled,
					server: serverAddr,
					key: streamKey,
					template: this.customPlatform ? 'custom' : this.platform.name
				};
			}

			// payload.serviceMeta = {}
			if (this.canConfigureMetadata()) {
				const {
					title,
					description,
					privacyStatus,
					game,
					game_id,
					persistent
				} = this.platform.configurableMetadata;
				const metaUpdates = {};
				if (title) {
					metaUpdates.title = title;
				}
				if (description) {
					metaUpdates.description = description;
				}
				if (game) {
					metaUpdates.game = game;
				}
				if (game_id) {
					metaUpdates.game_id = game_id;
				}
				if (persistent) {
					metaUpdates.persistent = persistent;
				}
				if (privacyStatus) {
					metaUpdates.privacyStatus = privacyStatus;
				}
				if (this.platform.name === 'youtube events') {
					metaUpdates.enableAdsOnStream = this.enableAdsOnStream;
					if (this.enableAdsOnStream) {
						metaUpdates.adFrequency = this.adFrequency;
					}
				}
				payload.serviceMeta = metaUpdates;
			}

			if (this.platform.streamCreationRequired) {
				let ingest;
				try {
					const metaprops = payload.serviceMeta;
					const params = {
						forceCreate: 1,
						streamTitle: metaprops.title,
						streamDescription: metaprops.description || metaprops.game,
						persistent: metaprops.persistent,
						title: metaprops.title,
						description: metaprops.description || metaprops.game,
						privacyStatus: metaprops.privacyStatus,
						enableAdsOnStream: metaprops.enableAdsOnStream,
						adFrequency: metaprops.adFrequency,
					};

					const subChannelId = this.platform.subChannel;
					if (subChannelId) {
						params.subChannel = subChannelId;
						payload.serviceMeta.subChannel = subChannelId;

						// let subChannelOriginalId = _.split(subChannelId, '@').pop()
						const [subChannelType, subChannelOriginalId] = _.split(subChannelId, '@');
						const subChannelObject = this.platform.subChannelList.find((c) => {
							const chId = String(c.id);
							return chId !== '0' && chId === subChannelOriginalId;
						});

						if (subChannelObject) {
							payload.serviceMeta.subChannelName = subChannelObject.name;

							// Add scheduleTime to prevent the BE override the current schedule time
							params.scheduleTime = subChannelObject.scheduleTime;
						}

						if (subChannelType === '-' && metaprops.title) {
							payload.serviceMeta.subChannelName = metaprops.title;
						}
					}

					// We use POST request here to avoid the case of the querystring too large, will be get the 414 error code from the back-end side
					ingest = await IntegrationService.getServiceIngest(
						this.linkedServiceCreds,
						params,
						'post'
					);
				} catch (e) {
					console.log(e);
					this.processing = false;

					const wasFacebookGroup =
						/facebook/g.test(this.platform.name || '') &&
						this.selectedSubChannel.type === 'group';
					if (wasFacebookGroup) {
						this.showFacebookGroupsGuide();
					} else {
						window.alert(
							'Unable to retrieve the ingest configuration. Please try again.' +
							(_.get(e, 'message') || e)
						);
					}
				}

				if (!ingest) {
					this.serviceLinkError = true;
					return;
				}

				this.linkedServiceCreds = ingest._id;
				payload.key = utils.resolveStreamKey(ingest.key, true);
				payload.server = utils.resolveURL(ingest.server, true);
				payload.ignoreServiceMetaUpdates = true;

				if (ingest.subChannelId) {
					payload.serviceMeta.subChannel = ingest.subChannelId;
				}
			}

			if (this.linkedServiceCreds) {
				payload.enabled = false;
				payload.linkedServiceCreds = this.linkedServiceCreds;
			}

			try {
				const platform = await StreamService.addStreamPlatform(
					this.stream._id,
					payload
				);
				track(EVENTS.DESTINATION_ADDED, {
					streamType: this.stream.type,
					destinationTemplate: platform.template,
					isCustom: platform.template === 'custom'
				});
				this.$emit('platform-saved', platform, this.selectedGroup);
				// Clear platform usage cache to fetch fresh data after adding platform
				this.$store.commit('PlatformUsage/CLEAR_USAGE', this.stream.type);
				this.fetchPlatformUsage();
				setTimeout(() => this.dismiss(), 600);
			} catch (err) {
				this.error = err;
				this.processing = false;
				// check if subscripiton error
				if (err && _.indexOf(err.message, 'subscription') > -1) {
					this.error.subscription = true;
				}
			}
		},
		dismiss() {
			// hideLegacyModal() now broadcasts on the modal bus, so every
			// instance with this id closes (no stray stuck overlay).
			this.hideLegacyModal();
			this.onDismiss();
		},
		modifySrtUrl(url) {
			if (url?.startsWith('srt://')) return url
			return 'srt://' + url
		},
		validateForm() {
			let props = ['server', 'streamKey'];
			let fields = this.platform

			if (this.customMode === 'SRT' && this.customPlatform) {
				props = ['host', 'port']
				fields = this.customSrtPlatform
			}

			let valids = 0;
			_.each(props, prop => {
				let val = fields[prop];
				if (prop === 'server') {
					let { valid } = utils.validateURL(val, { validatePathname: false });
					if (valid) {
						const platformName = this.platform?.name?.toLowerCase() || '';
						if (platformName.includes('tiktok')) {
							valid = val.includes('tiktokcdn');
						} else if (platformName.includes('instagram')) {
							valid = val.includes('fbcdn.net') || val.includes('instagram.com');
						} else if (platformName.includes('kick')) {
							valid = val.includes('global-contribute');
						}
					}
					if (!valid) val = null;
				}

				if (val) valids++;
				// else this.formErrors[prop] = true;

				this.formErrors[prop] = !val;
			});

			let srtValidation = true
			if (this.customMode === 'SRT') {
				if (this.customSrtPlatform.host.includes(`:${this.customSrtPlatform.port}`)) {
					this.customSrtPlatform.host = this.customSrtPlatform.host.replace(`:${this.customSrtPlatform.port}`, '')
				}

				srtValidation = srtValidation && !this.customSrtPlatformErrors.port
				srtValidation = srtValidation && !this.customSrtPlatformErrors.latency
				srtValidation = srtValidation && !this.customSrtPlatformErrors.passphrase
			}

			return valids === props.length && srtValidation;
		},
		onDismiss() {
			// clear up cache
			this.resetPlatform();
			this.clearErrors();

			// lazy clear
			setTimeout(() => {
				this.stage = 0;
				this.processing = false;
			}, 1000);
		},
		clearErrors() {
			this.error = null;
			this.formErrors.server = false;
			this.formErrors.streamKey = false;
			this.formErrors.host = false;
			this.formErrors.port = false;
			this.formErrors.streamId = false;

			this.customSrtPlatformErrors = {
				host: '',
				latency: '',
				port: '',
				server: '',
				streamId: '',
				streamKey: '',
				passphrase: ''
			}
		},
		resetPlatform() {
			this.platform = { enabled: false };
			this.serviceLinkError = false;
			this.linkedServiceCreds = null;
			this.serviceLinkProcessing = false;
			this.rtmpAuthUsername = '';
			this.rtmpAuthPassword = '';
			this.internalWorkflow = false;
			this.addMode = 'new';
			this.savedDeleteConfirmId = null;
			this.creatingSaved = false;
			this.newSaved = { name: '', server: '', key: '' };
			this.justCreatedId = null;
			this.renamingId = null;
			this.editValues = { name: '', server: '', key: '' };

			this.customMode = 'RTMP'
			this.customSrtPlatform = {
				host: '',
				port: '',
				streamId: '',
				latency: 120,
				passphrase: '',
				pids: '',
				pmtPid: ''
			}
			this.enableAdsOnStream = false;
		},
		checkPassword() {
			if (this.rtmpAuthPassword.includes('@')) {
				this.isRtmpPassValid = false
			} else this.isRtmpPassValid = true
		},
		isError(field) {
			return this.formErrors[field] || this.customSrtPlatformErrors[field]?.length > 0 ? true : false
		},
		errorText(field) {
			if (this.formErrors[field]) return 'Required field'
			if (this.customSrtPlatformErrors[field].length > 0) return this.customSrtPlatformErrors[field]
		},
		onCastrStreamPlatformSaved(platform) {
			this.$emit('platform-saved', platform);
			// Close immediately and reliably via dismiss() (global bus close).
			// Relying on a delayed timer here was fragile: emitting
			// `platform-saved` triggers a parent refresh that can re-render and
			// disrupt the pending close, leaving the modal/backdrop stuck open.
			this.dismiss();
		},
		showFacebookIcon(subchannel) {
			return this.platform?.name?.toLowerCase() === 'facebook live' && subchannel?.type?.toLowerCase() === 'page'
		}
	},
};
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.facebooklive-disable-plat,
.youtubeevents-disable-plat {
	pointer-events: none;
	opacity: .4;
}

.error {
	color: #fff;
}

.modal-container {
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.platform-list {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	padding: 0 12px 16px;
	position: relative;
}

.platform-list>li:empty {
	display: none;
}

.platform-item {
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	border-radius: 6px;
	cursor: pointer;
}

.platform-item.active {
	background-color: var(--c-main-1);
}

.internal-workflow.active {
	background-color: var(--c-main-1);
}

.platform-icon img {
	max-height: 40px;
	object-fit: contain;
	margin-bottom: .5rem;
	width: 100%;
}

.platform-icon_secondary {
	max-height: 40px;
	object-fit: contain;
}

.second-modal {
	position: fixed;
	top: 0;
	left: 510px;
	background-color: var(--c-dark-2);
	width: 410px;
	height: 100%;
	z-index: 51;
	opacity: 0;
	visibility: hidden;
	pointer-events: none;
	/* Calm, fast fade (no movement) — feels solid and avoids the shadow-repaint
	   jank a sliding panel causes. visibility delayed so the fade-out completes. */
	transition: opacity 0.18s ease-out, visibility 0s linear 0.18s;
	/* soft edge shadow for separation instead of hard border lines */
	box-shadow: -16px 0 40px rgba(0, 0, 0, 0.35);
}

.second-modal.showform {
	opacity: 1;
	visibility: visible;
	pointer-events: auto;
	transition: opacity 0.18s ease-out;
}

/* Crossfade when switching between platforms while the panel stays open.
   Pure opacity (no movement) for a calm, solid feel. */
.second-modal-swap-enter-active,
.second-modal-swap-leave-active {
	transition: opacity 0.15s ease;
}

.second-modal-swap-enter-from,
.second-modal-swap-leave-to {
	opacity: 0;
}

.second-modal-content {
	height: 100%;
	overflow-x: hidden;
	overflow-y: auto;
	padding-bottom: 20px;
}

.expand.second-modal.showform {
	width: 510px;
}

#service-subchannels-dropdown .hint-upgrade {
	display: none;
}

#service-subchannels-dropdown .disabled .hint-upgrade {
	display: block;
}

:deep(* .dropdown-menu_md),
.tests {
	max-height: 300px;
	overflow-y: auto;
	max-width: 100%;
}

:deep(.page-item.dropdown-item) {
	white-space: normal;
}

.platform-label {
	text-transform: capitalize;
	text-align: center;
}

.add-platform-title {
	font-size: 20px;
}

.platform-item:hover {
	background-color: var(--c-dark-3);
}

.doc img {
	max-width: 16px;
	width: 100%;
	transform: translateY(-1px);
}

@media (max-width: 767px) {
	.showform.second-modal {
		top: 70px;
		left: 0;
		z-index: 1030;
		max-width: 100%;
		height: calc(100dvh - 70px);
	}
}
</style>
